import { useEffect, useState } from "react";
import type { Profile } from "../api/types";

const DAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
export function BriefingSchedule({ profile, save, pending }: {
  profile: Profile; save: (change: { id: string } & Record<string, unknown>) => void; pending: boolean;
}) {
  const initial = () => ({
    preset: profile.briefing_schedule_preset ?? "weekly",
    time: profile.briefing_schedule_time ?? "07:00",
    days: String(profile.briefing_schedule_every_days ?? 7),
    weekday: profile.briefing_schedule_weekday ?? 1,
    monthday: profile.briefing_schedule_monthday ?? 1,
  });
  const [plan, setPlan] = useState(initial);
  useEffect(() => setPlan(initial()), [profile]);
  const change = (value: Partial<typeof plan>) => setPlan(p => ({ ...p, ...value }));
  const monthly = ["monthly", "quarterly"].includes(plan.preset);
  const valid = /^\d+$/.test(plan.days) && Number(plan.days) >= 1 && Number(plan.days) <= 365;
  const unchanged = JSON.stringify(plan) === JSON.stringify(initial());
  return <>
    <label className="check-row">
      <input type="checkbox" checked={profile.weekly_briefing} disabled={pending}
        onChange={e => save({ id: profile.id, weekly_briefing: e.target.checked })} />
      <span>Wochenbriefing per E-Mail erhalten</span>
    </label>
    <div className="field-row schedule-fields">
      <div className="field">
        <label className="field-label" htmlFor="briefing-preset">Briefing-Intervall</label>
        <select id="briefing-preset" value={plan.preset} onChange={e => change({ preset: e.target.value as typeof plan.preset })}>
          <option value="days">Alle X Tage</option><option value="weekly">Wöchentlich</option>
          <option value="monthly">Monatlich</option><option value="quarterly">Quartalsweise</option>
          <option value="manual">Nur manuell</option>
        </select>
      </div>
      {plan.preset === "days" && <div className="field">
        <label className="field-label" htmlFor="briefing-days">Briefing-Abstand in Tagen</label>
        <input id="briefing-days" type="number" min={1} max={365} step={1} inputMode="numeric"
          value={plan.days} aria-invalid={!valid} onChange={e => change({ days: e.target.value })} />
        {!valid && <p className="hint" role="alert">Bitte 1 bis 365 ganze Tage angeben.</p>}
      </div>}
      {plan.preset === "weekly" && <div className="field">
        <label className="field-label" htmlFor="briefing-weekday">Briefing-Wochentag</label>
        <select id="briefing-weekday" value={plan.weekday} onChange={e => change({ weekday: Number(e.target.value) })}>
          {DAYS.map((d, i) => <option key={d} value={i + 1}>{d}</option>)}
        </select>
      </div>}
      {monthly && <div className="field">
        <label className="field-label" htmlFor="briefing-monthday">Briefing-Tag des Monats</label>
        <select id="briefing-monthday" value={plan.monthday} onChange={e => change({ monthday: Number(e.target.value) })}>
          {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}. des Monats</option>)}
          <option value={0}>Letzter Tag des Monats</option>
        </select>
      </div>}
      <div className="field">
        <label className="field-label" htmlFor="briefing-time">Briefing-Uhrzeit</label>
        <input id="briefing-time" type="time" value={plan.time} disabled={plan.preset === "manual"}
          onChange={e => change({ time: e.target.value })} />
      </div>
    </div>
    {monthly && plan.monthday > 28 && <p className="hint">In kürzeren Monaten am letzten Tag.</p>}
    <p className="hint">{unchanged ? profile.briefing_schedule_preview : "Noch nicht gespeichert."}</p>
    {unchanged && profile.next_briefing_at && <p className="hint">Nächster Versand: {new Date(profile.next_briefing_at).toLocaleString("de-DE")}</p>}
    <div className="action-bar">
      <button className="btn" disabled={pending || (plan.preset === "days" && !valid) || (plan.preset !== "manual" && !plan.time)}
        onClick={() => save({
          id: profile.id, briefing_schedule_preset: plan.preset,
          ...(plan.time ? { briefing_schedule_time: plan.time } : {}),
          ...(plan.preset === "days" ? { briefing_schedule_every_days: Number(plan.days) } : {}),
          ...(plan.preset === "weekly" ? { briefing_schedule_weekday: plan.weekday } : {}),
          ...(monthly ? { briefing_schedule_monthday: plan.monthday } : {}),
        })}>Briefing-Laufplan speichern</button>
    </div>
  </>;
}
